import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { X } from 'lucide-react';
import { useStore } from '@shared/hooks/useStore';
import { OpenBook } from '@shared/types/reader';
import defaultCover from '@shared/assets/images/default-cover.png';
import { formatPublicationTitle } from '@shared/utils/publication';

interface ReaderTabBarProps {
    onTabClick?: (publicationId: string) => void,
    onTabClose?: (publicationId: string) => void
}

const ReaderTabBar = observer(({ onTabClick, onTabClose }: ReaderTabBarProps) => {
    const { readerStore } = useStore();
    const { openBooks, activeBookId } = readerStore;

    useEffect(() => {
        if (activeBookId) {
            const activeElement = document.getElementById(`tab-${activeBookId}`);
            if (activeElement) {
                activeElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'center'
                });
            }
        }
    }, [activeBookId, openBooks.length]);

    const handleTabClick = (book: OpenBook) => {
        readerStore.setActiveBook(book.publication.id);
        onTabClick?.(book.publication.id);
    };

    const handleTabClose = (e: React.MouseEvent, publicationId: string) => {
        e.stopPropagation();
        readerStore.removeBook(publicationId);
        onTabClose?.(publicationId);
    };

    const getTabClass = (isActive: boolean) => {
        const base = 'flex gap-1 px-1.5 py-1 sm:px-2.5 sm:py-1 rounded-[10px] cursor-pointer w-[140px] sm:w-[209px] transition-all text-white';
        return isActive ?
            `${base} bg-[#FFFFFF1A]` :
            `${base} bg-[#7171711A] hover:bg-[#FFFFFF1A]`;
    };

    if (openBooks.length === 0) return null;

    return (
        <div className='flex items-center overflow-x-auto reader-tab-scrollbar'>
            <div className='flex items-center gap-[10px]'>
                {openBooks.map((book) => {
                    const isActive = book.publication.id === activeBookId;
                    const publication = book.publication;
                    return (
                        <div
                            key={publication.id}
                            id={`tab-${publication.id}`}
                            onClick={() => handleTabClick(book)}
                            className={getTabClass(isActive)}
                        >
                            <img
                                src={publication.linkImg}
                                alt={publication.title}
                                className='w-[24px] h-[32px] object-cover flex-shrink-0'
                                onError={(e) => {
                                    e.currentTarget.src = defaultCover;
                                }}
                            />
                            <div className='flex-1 min-w-0'>
                                <p className='text-xs font-semibold leading-[16px] truncate mb-1'>
                                    {formatPublicationTitle(publication)}
                                </p>
                                <p className='text-[11px] leading-[12px] hidden sm:block'>
                                    {book.currentPage || 1}
                                    /
                                    {publication.totalNumberPages}
                                    ページ
                                </p>
                            </div>
                            <button
                                onClick={e => handleTabClose(e, publication.id)}
                                className='p-1 flex-shrink-0 self-start'
                            >
                                <X size={14} />
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
});

export default ReaderTabBar;
